import React, { useState, useEffect } from 'react';
import { AssetData, assetService } from '../../services/assetService';
import { SectionData, sectionService } from '../../services/sectionService';
import { getCurrentConfig } from '../../config';
import { useToast } from '../ui/Toast';
import { permissionChecks } from '../../lib/auth/helpers';

export const AssetsManagement: React.FC = () => {
  const [assets, setAssets] = useState<AssetData[]>([]);
  const [sections, setSections] = useState<SectionData[]>([]);
  // Local state for sections as fallback
  const [localSections, setLocalSections] = useState<any[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState<AssetData | null>(null);
  const [viewingAsset, setViewingAsset] = useState<AssetData | null>(null);
  const [loading, setLoading] = useState(false);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(true); // Toggle for active/inactive assets
  
  const [formData, setFormData] = useState({
    section_id: '',
    asset_name: '',
    purpose: '',
    description: ''
  });
  
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const { showToast } = useToast();

  useEffect(() => {
    loadAssets();
    loadSections();
  }, []);

  const loadAssets = async () => {
    try {
      setLoading(true);
      const allAssets = await assetService.getAllAssets();
      setAssets(allAssets);
    } catch (error) {
      console.error('Failed to load assets:', error);
      showToast('Failed to load assets', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadSections = async () => {
    try {
      setSectionsLoading(true);
      
      // Try the section service first
      const allSections = await sectionService.getAllSections();
      setSections((Array.isArray(allSections) ? allSections : []).filter(s => s.is_active));
      
      // Also try direct API call as fallback
      try {
        const baseUrl = getCurrentConfig().apiBaseUrl;
        const response = await fetch(`${baseUrl}/sections`, {
          headers: { 'Content-Type': 'application/json' }
        });
        if (response.ok) {
          const result = await response.json();
          const sectionsData = result?.data?.sections || result?.data || result || [];
          const activeSections = Array.isArray(sectionsData) ? sectionsData.filter(s => s.is_active) : [];
          setLocalSections(activeSections);
        }
      } catch (directError) {
        console.error('Direct sections API call failed:', directError);
      }
      
    } catch (error) {
      console.error('Failed to load sections:', error);
      showToast('Failed to load sections', 'error');
    } finally {
      setSectionsLoading(false);
    }
  };

  // Use local data as fallback if store data is empty
  const availableSections = sections.length > 0 ? sections : localSections;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    
    if (!formData.section_id) {
      errors.section_id = 'Please select a section';
    }
    
    const nameValidation = assetService.validateAssetName(formData.asset_name);
    if (!nameValidation.isValid) {
      errors.asset_name = nameValidation.message || 'Invalid asset name';
    }
    
    // Purpose is optional, but validate if provided
    if (formData.purpose.trim()) {
      const purposeValidation = assetService.validatePurpose(formData.purpose);
      if (!purposeValidation.isValid) {
        errors.purpose = purposeValidation.message || 'Invalid purpose';
      }
    }
    
    const descValidation = assetService.validateDescription(formData.description);
    if (!descValidation.isValid) {
      errors.description = descValidation.message || 'Invalid description';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const submitData = {
        section_id: parseInt(formData.section_id),
        asset_name: formData.asset_name,
        purpose: formData.purpose.trim() || undefined,
        description: formData.description.trim() || undefined
      };
      
      if (editingAsset) {
        await assetService.updateAsset(editingAsset.asset_id, submitData);
        showToast('Asset updated successfully!', 'success');
      } else {
        await assetService.createAsset(submitData);
        showToast('Asset created successfully!', 'success');
      }
      
      resetForm();
      await loadAssets();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save asset';
      setFormErrors({ submit: errorMessage });
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (asset: AssetData) => {
    setEditingAsset(asset);
    setFormData({
      section_id: asset.section_id.toString(),
      asset_name: asset.asset_name,
      purpose: asset.purpose || '',
      description: asset.description || ''
    });
    setShowAddForm(true);
    setFormErrors({});
  };

  const handleToggleStatus = async (asset: AssetData) => {
    if (loading) return;
    
    setLoading(true);
    try {
      // Call the toggle API endpoint
      const baseUrl = getCurrentConfig().apiBaseUrl;
      const response = await fetch(`${baseUrl}/assets/${asset.asset_id}/toggle?cascade=true`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      showToast(`Asset ${!asset.is_open ? 'opened' : 'closed'} successfully!`, 'success');
      
      // Hard refresh to load the data post toggle
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadAssetsWithForceRefresh();
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to toggle asset status';
      console.error('Asset toggle error:', error);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAssetsWithForceRefresh = async () => {
    try {
      // Use direct API call to force fresh data
      const baseUrl = getCurrentConfig().apiBaseUrl;
      const response = await fetch(`${baseUrl}/assets`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Extract assets from nested structure
      const allAssets = result?.data?.assets || result?.data || result || [];
      setAssets(allAssets);
    } catch (error) {
      console.error('Failed to force refresh assets:', error);
      // Fallback to regular load
      await loadAssets();
    }
  };

  const handleView = (asset: AssetData) => {
    setViewingAsset(asset);
  };

  const handleDelete = async (asset: AssetData) => {
    if (!window.confirm(`Are you sure you want to delete "${asset.asset_name}"?`)) {
      return;
    }
    
    setLoading(true);
    try {
      await assetService.deleteAsset(asset.asset_id);
      showToast('Asset deleted successfully!', 'success');
      await loadAssets();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete asset';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      section_id: '',
      asset_name: '',
      purpose: '',
      description: ''
    });
    setFormErrors({});
    setShowAddForm(false);
    setEditingAsset(null);
  };

  const getSectionName = (sectionId: number) => {
    const section = (Array.isArray(sections) ? sections : []).find(s => s.section_id === sectionId);
    return section ? section.section_name : `Section ${sectionId}`;
  };

  const filteredAssets = showActiveOnly 
    ? (Array.isArray(assets) ? assets : []).filter(asset => asset.is_active)
    : (Array.isArray(assets) ? assets : []);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Assets Management</h1>
          <p className="text-gray-400 text-sm">Manage system assets and resources</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-start sm:items-center">
          {/* Active/Inactive Toggle */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-300">Show:</span>
            <button
              onClick={() => setShowActiveOnly(!showActiveOnly)}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                showActiveOnly 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-gray-600 hover:bg-gray-700 text-white'
              }`}
            >
              {showActiveOnly ? 'Active Only' : 'All Assets'}
            </button>
          </div>
          {permissionChecks.canUpdateAssets() && (
            <button 
              onClick={() => setShowAddForm(!showAddForm)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors text-sm font-medium"
            >
              {showAddForm ? 'Cancel' : 'Add Asset'}
            </button>
          )}
        </div>
      </div>
      
      {/* Add/Edit Asset Form */}
      {showAddForm && permissionChecks.canUpdateAssets() && (
        <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingAsset ? 'Edit Asset' : 'Add New Asset'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Section Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Section <span className="text-red-400">*</span>
              </label>
              <select 
                name="section_id"
                value={formData.section_id}
                onChange={handleInputChange}
                className={`w-full px-3 py-3 bg-gray-700 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.section_id ? 'border-red-500' : 'border-gray-600'
                }`}
                disabled={loading || sectionsLoading}
              >
                <option value="">Select a section</option>
                {availableSections.map((section) => (
                  <option key={section.section_id} value={section.section_id}>
                    {section.section_name}
                  </option>
                ))}
              </select>
              {formErrors.section_id && (
                <p className="mt-1 text-sm text-red-400">{formErrors.section_id}</p>
              )}
              {sectionsLoading && (
                <p className="mt-1 text-xs text-gray-400">Loading sections...</p>
              )}
            </div>

            {/* Asset Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Asset Name <span className="text-red-400">*</span>
              </label>
              <input 
                type="text" 
                name="asset_name"
                value={formData.asset_name}
                onChange={handleInputChange}
                className={`w-full px-3 py-3 bg-gray-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.asset_name ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="Enter asset name (e.g., X-Ray Scanner, Metal Detector)" 
                disabled={loading}
              />
              {formErrors.asset_name && (
                <p className="mt-1 text-sm text-red-400">{formErrors.asset_name}</p>
              )}
            </div>

            {/* Purpose */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Purpose
              </label>
              <input 
                type="text" 
                name="purpose"
                value={formData.purpose}
                onChange={handleInputChange}
                className={`w-full px-3 py-3 bg-gray-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.purpose ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="Enter asset purpose (e.g., Baggage screening, Passenger detection)" 
                disabled={loading}
                maxLength={200}
              />
              {formErrors.purpose && (
                <p className="mt-1 text-sm text-red-400">{formErrors.purpose}</p>
              )}
              <p className="mt-1 text-xs text-gray-400">Maximum 200 characters</p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea 
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className={`w-full px-3 py-3 bg-gray-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${
                  formErrors.description ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="Enter asset description (optional)" 
                disabled={loading}
              />
              {formErrors.description && (
                <p className="mt-1 text-sm text-red-400">{formErrors.description}</p>
              )}
              <p className="mt-1 text-xs text-gray-400">Maximum 500 characters</p>
            </div>

            {/* Submit Error */}
            {formErrors.submit && (
              <div className="p-3 bg-red-900/50 border border-red-500 rounded-md">
                <p className="text-sm text-red-400">{formErrors.submit}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button 
                type="submit" 
                disabled={loading}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white rounded transition-colors font-medium"
              >
                {loading ? (editingAsset ? 'Updating...' : 'Creating...') : (editingAsset ? 'Update Asset' : 'Create Asset')}
              </button>
              <button 
                type="button" 
                onClick={resetForm}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Asset List */}
      <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
        <h3 className="text-lg font-semibold mb-4">Asset List</h3>
        {loading && !showAddForm ? (
          <div className="text-center py-8">
            <div className="loading-spinner mx-auto mb-2"></div>
            <p className="text-gray-400">Loading assets...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredAssets.map((asset) => (
              <div key={asset.asset_id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 bg-gray-700 rounded space-y-2 sm:space-y-0">
                <div className="flex-1">
                  <div className="font-medium text-white">{asset.asset_name}</div>
                  <div className="text-sm text-gray-400">
                    Section: {getSectionName(asset.section_id)}
                  </div>
                  <div className="text-sm text-yellow-300">Purpose: {asset.purpose}</div>
                  {asset.description && (
                    <div className="text-sm text-gray-400 mt-1">{asset.description}</div>
                  )}
                  <div className="text-xs text-gray-500 mt-1">
                    Updated: {new Date(asset.updated_date || asset.created_date).toLocaleDateString()} by {asset.updated_by}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  {/* Status Toggle */}
                  {permissionChecks.canUpdateAssets() && (
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-400">Status</span>
                      <button
                        onClick={() => handleToggleStatus(asset)}
                        disabled={loading}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 ${
                          loading
                            ? 'bg-gray-500 cursor-not-allowed'
                            : asset.is_open 
                            ? 'bg-green-600 focus:ring-green-500' 
                            : 'bg-gray-600 focus:ring-gray-500'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            asset.is_open ? 'translate-x-6' : 'translate-x-1'
                          } ${loading ? 'animate-pulse' : ''}`}
                        />
                      </button>
                      <span className={`text-xs font-medium ${asset.is_open ? 'text-green-400' : 'text-gray-400'}`}>
                        {asset.is_open ? 'Open' : 'Closed'}
                      </span>
                    </div>
                  )}
                  
                  {/* Edit Button */}
                  {permissionChecks.canUpdateAssets() && (
                    <button 
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs transition-colors"
                      onClick={() => handleEdit(asset)}
                      disabled={loading}
                    >
                      Edit
                    </button>
                  )}
                  
                  {/* View Button - Always visible if user can view assets */}
                  <button 
                    className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs transition-colors"
                    onClick={() => handleView(asset)}
                    disabled={loading}
                  >
                    View
                  </button>
                </div>
              </div>
            ))}
            {filteredAssets.length === 0 && !loading && (
              <div className="text-center py-8 text-gray-400">
                No assets found. Create your first asset to get started.
              </div>
            )}
          </div>
        )}
      </div>

      {/* View Asset Modal */}
      {viewingAsset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Asset Details</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-400">Name</label>
                <p className="text-white">{viewingAsset.asset_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">Section</label>
                <p className="text-white">{getSectionName(viewingAsset.section_id)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">Purpose</label>
                <p className="text-yellow-300">{viewingAsset.purpose}</p>
              </div>
              {viewingAsset.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-400">Description</label>
                  <p className="text-white">{viewingAsset.description}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400">Active</label>
                  <p className={viewingAsset.is_active ? 'text-blue-400' : 'text-gray-400'}>
                    {viewingAsset.is_active ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">Open</label>
                  <p className={viewingAsset.is_open ? 'text-green-400' : 'text-red-400'}>
                    {viewingAsset.is_open ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">Last Updated</label>
                <p className="text-white">
                  {new Date(viewingAsset.updated_date || viewingAsset.created_date).toLocaleString()} by {viewingAsset.updated_by}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">Created</label>
                <p className="text-white">{new Date(viewingAsset.created_date).toLocaleString()}</p>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button 
                onClick={() => setViewingAsset(null)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
